<template>
	<div class="container">
		<div class="page page-inventory">
			<!-- Header -->
			<div class="page-header d-flex justify-content-between">
				<h1 class="page-title">{{ $t('Revenue Stream Monitoring') }}</h1>
				<daterangepicker
					:presets="filterDatePresets"
					:start="since"
					:end="until"
					@selected="setFilterDate">
				</daterangepicker>
			</div>

			<!-- Revenue Summary Cards -->
			<div class="inventory-summaries row">
				<div class="col-md-3">
					<div class="box box--primary mh-120">
						<div class="m-2">
							<h4
								class="text-accent font-weight-bold mb-0"
								:class="{ 'is-loading': isFetchingSummary }">
								<span>{{ currency }} {{ totalRevenueDisplay }}</span>
							</h4>
							<span class="d-block mt-2"><i class="bi bi-wallet"></i> {{ $t('Total Revenue') }}</span>
						</div>
					</div>
				</div>
				<div class="col-md-3">
					<div class="box box--primary mh-120">
						<div class="m-2">
							<h4
								class="text-accent font-weight-bold mb-0"
								:class="{ 'is-loading': isFetchingSummary }">
								<span>{{ currency }} {{ serviceFeeDisplay }}</span>
							</h4>
							<span class="d-block mt-2"><i class="bi bi-wallet2"></i> {{ $t('Service Fee') }}</span>
						</div>
					</div>
				</div>
				<div class="col-md-3">
					<div class="box box--primary mb-md-0 mh-120">
						<div class="m-2">
							<h4
								class="text-accent font-weight-bold mb-0"
								:class="{ 'is-loading': isFetchingSummary }">
								<span>{{ currency }} {{ handlingFeeDisplay }}</span>
							</h4>
							<span class="d-block mt-2"><i class="bi bi-wallet2"></i> {{ $t('Handling Fee') }}</span>
						</div>
					</div>
				</div>
				<div class="col-md-3">
					<div class="box box--primary mb-md-0 mh-120">
						<div class="m-2">
							<h4
								class="text-accent font-weight-bold mb-0"
								:class="{ 'is-loading': isFetchingSummary }">
								<span>{{ currency }} {{ productCommissionDisplay }}</span>
							</h4>
							<span class="d-block mt-2"><i class="bi bi-wallet2"></i> {{ $t('Product Commission') }}</span>
						</div>
					</div>
				</div>
			</div>

			<hr />

			<!-- Filters -->
			<div class="table-list-menu d-lg-flex justify-content-between">
				<div class="table-filters d-flex align-items-center">
					<!-- Search -->
					<input
						type="text"
						class="form-control mr-2"
						style="max-width: 250px;"
						placeholder="Search Order ID"
						v-model="keyword"
						@input="onKeywordChange" />
				</div>
				<div class="table-filters d-flex align-items-center">
					<!-- Clear Filter -->
					<button
						type="button"
						class="btn btn-plain mr-2"
						@click="clearFilter"
						v-if="isFilterOn">
						{{ $t('Clear Filter') }}
					</button>
					<!-- Export -->
					<button
						type="button"
						class="btn btn-light"
						:class="{ 'is-loading': isExporting }"
						@click="exportData">
						<i class="material-icons">save_alt</i>
						<span class="ml-1">{{ $t('Export') }}</span>
					</button>
				</div>
			</div>

			<!-- Revenue Transaction Table -->
			<div class="wallet-history-table table-responsive mt-3">
				<table class="table table-primary table-striped table-hover">
					<thead>
						<tr>
							<th width="5%">{{ $t('No') }}.</th>
							<th width="15%">{{ $t('Order ID') }}</th>
							<th width="25%">{{ $t('Description') }}</th>
							<th width="15%">{{ $t('Amount') }}</th>
							<th width="10%">{{ $t('Status') }}</th>
							<th width="15%">{{ $t('Completed Date') }}</th>
							<th></th>
						</tr>
					</thead>
					<tbody>
						<template v-for="(transaction, index) in transactions">
							<tr
								class="cur-p"
								:class="{ 'highlighted': expandedTransaction && expandedTransaction.order_id === transaction.order_id }"
								:key="`${index}-main`"
								@click="toggleDetails(transaction)">
								<td class="text-right">{{ (page - 1) * limit + index + 1 }}.</td>
								<td>
									<span class="text-bold text-primary">
										#{{ transaction.order_id }}
									</span>
								</td>
								<td>{{ transaction.description }}</td>
								<td class="text-right">
									<span class="text-success">+ {{ currency }} {{ formatNumberPlain(transaction.amount) }}</span>
								</td>
								<td class="text-center">
									<mark :class="`order-status status-${transaction.status}`">
										<span>{{ transaction.status }}</span>
									</mark>
								</td>
								<td class="text-center">
									<span>{{ formatDate(transaction.completed_at) }}</span>
								</td>
								<td class="text-center">
									<div class="d-flex align-items-center justify-content-end pr-4">
										<i class="material-icons cur-p" v-tooltip="'Show Details'">
											<template v-if="expandedTransaction && expandedTransaction.order_id === transaction.order_id">keyboard_arrow_up</template>
											<template v-else>keyboard_arrow_down</template>
										</i>
									</div>
								</td>
							</tr>
							<tr
								class="detail-view"
								:key="`${index}-detail`"
								v-if="expandedTransaction && expandedTransaction.order_id === transaction.order_id">
								<td colspan="7">
									<div class="p-3">
										<h5 class="mb-3">{{ $t('Breakdown Details') }}</h5>
										<table class="table table--small">
											<thead>
												<tr>
													<th class="text-left">Description</th>
													<th class="text-right">Value</th>
												</tr>
											</thead>
									<tbody>
										<tr>
											<td>Service Fee</td>
											<td class="text-right">{{ currency }} {{ formatNumberPlain(transaction.breakdown && transaction.breakdown.service_fee) }}</td>
										</tr>
										<tr>
											<td>Ostock Handling Fee</td>
											<td class="text-right">{{ currency }} {{ formatNumberPlain(transaction.breakdown && transaction.breakdown.ostock_handling_fee) }}</td>
										</tr>
										<tr>
											<td>Product Commission</td>
											<td class="text-right">{{ currency }} {{ formatNumberPlain(transaction.breakdown && transaction.breakdown.product_commission) }}</td>
										</tr>
									</tbody>
										</table>
									</div>
								</td>
							</tr>
						</template>
					</tbody>
				</table>
			</div>

			<!-- Table Loader -->
			<div class="table-fetch-status" v-if="isFetching">
				<div class="is-loading large dark m-auto"></div>
			</div>

			<!-- Table Empty -->
			<div class="list-empty" v-if="transactions.length === 0 && !isFetching">
				<span><i class="material-icons md-48 mr-2">error_outline</i></span>
				<span v-if="keyword === ''">There is no revenue data in this date range.</span>
				<span v-else>No transactions found</span>
			</div>

			<!-- Load More -->
			<div class="text-center mt-3" v-if="!isAll && !isFetching && transactions.length > 0">
				<button class="btn btn-light" @click="loadMore">
					{{ $t('Load More') }}
				</button>
			</div>
		</div>
	</div>
</template>

<script>
import { mapGetters } from 'vuex';
import dayjs from 'dayjs';
import Daterangepicker from '@/components/Daterangepicker.vue';
import { formatNumberPlain, downloadFile, getDateRange } from '@/lib/helper';
import * as revenueStreamApi from '@/api/revenue-stream';

const defaultFilterDate = getDateRange();

export default {
	name: 'AdminRevenueStream',
	components: {
		Daterangepicker,
	},
	data() {
		return {
			currency: 'Rp',
			filterDate: defaultFilterDate,
			keyword: '',
			limit: 25,
			page: 1,
			transactions: [],
			summary: {
				total_revenue: 0,
				service_fee: 0,
				handling_fee: 0,
				product_commission: 0,
			},
			expandedTransaction: null,
			isFetching: false,
			isFetchingSummary: false,
			isExporting: false,
			isAll: false,
		};
	},
	computed: {
		...mapGetters({
			user: 'user',
			filterDatePresets: 'filterDatePresets',
		}),
		since() {
			return this.$route.query.since || dayjs().startOf('month').format('YYYY-MM-DD');
		},
		until() {
			return this.$route.query.until || dayjs().endOf('month').format('YYYY-MM-DD');
		},
		totalRevenueDisplay() {
			return formatNumberPlain(this.summary.total_revenue);
		},
		serviceFeeDisplay() {
			return formatNumberPlain(this.summary.service_fee);
		},
		handlingFeeDisplay() {
			return formatNumberPlain(this.summary.handling_fee);
		},
		productCommissionDisplay() {
			return formatNumberPlain(this.summary.product_commission);
		},
		isFilterOn() {
			return this.keyword !== '';
		},
	},
	methods: {
		formatNumberPlain,
		formatDate(dateString) {
			if (!dateString) return '-';
			const format = 'DD-MM-YYYY - HH:mm';
			const timezone = (this.user && this.user.timezone) || 'Asia/Jakarta';
			return this.moment.utc(dateString).tz(timezone).format(format);
		},
		init() {
			this.initFilterDate();
			this.fetchSummary();
			this.fetchTransactions();
		},
		initFilterDate() {
			if (this.since && this.until) {
				this.filterDate = {
					start: this.since,
					end: this.until,
				};
			}
		},
		setFilterDate(filterDate) {
			this.filterDate = filterDate;
			const since = dayjs(filterDate.start).format('YYYY-MM-DD');
			const until = dayjs(filterDate.end).format('YYYY-MM-DD');
			const query = { ...this.$route.query };
			query.since = since;
			query.until = until;
			this.$router.push({ query });
			this.fetchSummary();
			this.fetchTransactions(true);
		},
		getFetchParams() {
			const params = {
				limit: this.limit,
				page: this.page,
				since: dayjs(this.filterDate.start).format('YYYY-MM-DD'),
				until: dayjs(this.filterDate.end).format('YYYY-MM-DD'),
			};
			if (this.keyword && this.keyword.trim()) {
				params.order_id = this.keyword.trim();
			}
			return params;
		},
		fetchSummary() {
			this.isFetchingSummary = true;
			const params = {
				since: dayjs(this.filterDate.start).format('YYYY-MM-DD'),
				until: dayjs(this.filterDate.end).format('YYYY-MM-DD'),
			};
			const callback = (response) => {
				this.isFetchingSummary = false;
				this.summary = response.data.data || this.summary;
			};
			const errorCallback = () => {
				this.isFetchingSummary = false;
			};
			revenueStreamApi.getRevenueSummary(params, callback, errorCallback);
		},
		fetchTransactions(reset = false) {
			this.isFetching = true;
			if (reset) {
				this.transactions = [];
				this.page = 1;
				this.isAll = false;
				this.expandedTransaction = null;
			}
			const params = this.getFetchParams();
			const callback = (response) => {
				this.isFetching = false;
				const newTransactions = response.data.data || [];
				this.transactions.push(...newTransactions);
				this.page++;
				if (newTransactions.length < this.limit) {
					this.isAll = true;
				}
			};
			const errorCallback = () => {
				this.isFetching = false;
				this.isAll = true;
			};
			revenueStreamApi.getRevenueTransactions(params, callback, errorCallback);
		},
		loadMore() {
			if (!this.isFetching && !this.isAll) {
				this.fetchTransactions();
			}
		},
		onKeywordChange() {
			clearTimeout(this.searchTimeout);
			this.searchTimeout = setTimeout(() => {
				this.fetchTransactions(true);
			}, 500);
		},
		clearFilter() {
			this.keyword = '';
			this.fetchTransactions(true);
		},
		toggleDetails(transaction) {
			if (this.expandedTransaction && this.expandedTransaction.order_id === transaction.order_id) {
				this.expandedTransaction = null;
			} else {
				this.expandedTransaction = transaction;
			}
		},
		exportData() {
			this.isExporting = true;
			const params = {
				since: this.since,
				until: this.until,
			};
			if (this.keyword && this.keyword.trim()) {
				params.order_id = this.keyword.trim();
			}
			const callback = (response) => {
				this.isExporting = false;
				const url = response.data.data;
				if (url) {
					downloadFile(url);
				} else {
					this.$notify({
						group: 'app',
						type: 'warn',
						title: 'Export',
						text: 'No data to export',
					});
				}
			};
			const errorCallback = () => {
				this.isExporting = false;
			};
			revenueStreamApi.exportRevenueTransactions(params, callback, errorCallback);
		},
	},
	watch: {
		user() {
			if (this.user) {
				this.init();
			}
		},
	},
	created() {
		if (this.user) {
			this.init();
		}
	},
};
</script>

<style scoped>
.mh-120 {
	min-height: 120px;
}
</style>
